import { useSelector } from "react-redux";
import Actions from "src/components/actionbar";
import React, { useContext, useEffect, useMemo } from "react";

import Slider from "src/components/slider";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import userRoleService from "src/utils/userRoles.service";
import { Podcast, PodcastTranscriptProcessing, PodcastValidationError } from "src/types/coaching/podcast";
import {
  deletePodcast,
  discardPodcastDraft,
  generateAudioForPodcast,
  invalidatePodcastDeletionState,
  invalidatePodcastUpdateState,
  quickGeneratePodcast,
} from "src/redux/actions/coaching/podcast";

import _ from "lodash";
import cx from "classnames";
import styles from "./menu.module.scss";
import { PodcastEditState } from "../studio";
import { preparePodcastConfigurationRequest } from "../../edit/helper";
import { PodcastContext } from "../../edit/context";
import { getContinueEditingTooltip } from "src/pages/coaching/utils/continueEditingTooltip";
import {
  buildEditEntryState,
  COACHING_STUDIO_PATH,
  getManageNavigationRootFrom,
  getManageNavigationState,
  getPublishedOverviewPath,
  isPublishedOverviewPath,
} from "src/pages/coaching/utils/manageNavigation";

export interface MenuState {
  primaryAction: "save" | "publish" | null;
  secondaryAction: "edit-studio" | null;
}

interface PodcastMenuBarProps {
  className?: string;
  podcast: Podcast;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;
  mode?: "editor" | "viewer";
}

export const PodcastMenuBar: React.FC<PodcastMenuBarProps> = ({
  className,
  podcast,
  menuState,
  onMenuStateChange,
  mode = "editor",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const id = podcast?.id;
  const podcastContext = useContext(PodcastContext);

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = React.useState(false);

  const showValidationErrorDialog = useMemo(() => {
    return podcastContext?.showValidationErrorDialog || false;
  }, [podcastContext]);

  const hasChanges = useMemo(() => {
    return _.get(podcast, `hasChanges`) as boolean;
  }, [podcast]);

  const continueEditingTooltip = useMemo(
    () => getContinueEditingTooltip(podcast),
    [podcast],
  );

  const emptyPodcast = useMemo(() => {
    return !_.get(podcast, "transcriptUrl", null);
  }, [podcast]);

  const shouldGenerateTranscript = useMemo(() => {
    const needsProcessing = _.get(podcast, "transcriptProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    return false;
  }, [podcast]);

  const shouldGenerateAudio = useMemo(() => {
    if (shouldGenerateTranscript) {
      return false;
    }

    const needsProcessing = _.get(podcast, "audioProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    return false;
  }, [podcast, shouldGenerateTranscript]);

  const canPublish = useMemo(() => {
    const transcriptProcessed = _.get(podcast, "transcriptProcessing.status") !== 'completed';
    if (transcriptProcessed) {
      return false;
    }

    const audioProcessing = _.get(podcast, "audioProcessing.status") !== 'completed';
    if (audioProcessing) {
      return false;
    }

    return true;
  }, [podcast]);

  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(
      state.coaching,
      `podcasts.update.['${id}'].updating`,
    ) as boolean;
    const updated = _.get(
      state.coaching,
      `podcasts.update.['${id}'].updated`,
    ) as boolean;

    return { updating, updated };
  });

  const { publishing, published } = useSelector((state: any) => {
    const publishing = _.get(
      state,
      `coaching.podcasts.publish.publishing`,
    ) as boolean;
    const published = _.get(
      state,
      `coaching.podcasts.publish.published`,
    ) as boolean;

    return { publishing, published };
  });

  const errors = useSelector((state: any) => {
    return _.size(_.get(state.coaching, "podcasts.edit.errors"));
  }, _.isEqual);

  const quickGenerating = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const processingTranscript = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateTranscript.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.transcriptProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const processingAudio = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateAudio.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.audioProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const showGenerateTranscriptButton = shouldGenerateTranscript || processingTranscript;
  const showGenerateAudioButton = (shouldGenerateAudio || processingAudio) && !showGenerateTranscriptButton;

  const generating = quickGenerating || processingTranscript || processingAudio;

  const handleSave = () => {
    onMenuStateChange({
      ...menuState,
      primaryAction: "save",
    });
  };

  const handlePublish = () => {
    if (errors > 0) {
      podcastContext.setShowValidationErrorDialog?.(true);
      return;
    }

    onMenuStateChange({
      ...menuState,
      primaryAction: "publish",
    });
  };

  const handleDeleteFinish = () => {
    navigate("/coaching/studio");
    setShowDeleteDialog(false);
  };

  const handleDiscardFinish = () => {
    setShowDiscardDialog(false);
  };

  const handleEditStudio = () => {
    if (
      podcast.status === "PUBLISHED" &&
      !isPublishedOverviewPath(getManageNavigationState(location.state).from ?? "")
    ) {
      const rootFrom = getManageNavigationRootFrom(location.state);
      const returnToPath =
        rootFrom === COACHING_STUDIO_PATH
          ? COACHING_STUDIO_PATH
          : getPublishedOverviewPath("podcast", podcast.id!);

      navigate(`/coaching/manage/podcasts/draft/${podcast.id}`, {
        state: buildEditEntryState(returnToPath, location.state),
        replace: true,
      });
    }

    onMenuStateChange({
      ...menuState,
      secondaryAction: "edit-studio",
    });
  }

  const handleGenerateTranscript = () => {
    const request = preparePodcastConfigurationRequest({}, podcast);
    dispatch(quickGeneratePodcast({
      id: podcast.id!,
      request: request,
      generateAudio: true,
    }));
  };

  const handleGenerateAudio = () => {
    dispatch(generateAudioForPodcast({
      id: podcast.id!,
      request: {},
    }));
  };

  const handlePreview = () => {
    window.open(`/coaching/manage/podcasts/preview/${podcast.id}`, "_blank");
  };

  const handleEdit = () => {
    navigate(`/coaching/manage/podcasts/draft/${podcast.id}`, {
      state: buildEditEntryState(location.pathname, location.state),
    });
  };

  const ViewerActions = () => {
    const showContinueEditing =
      podcast.status === "PUBLISHED" && hasChanges;

    return (
      <>
        {userRoleService.canCreateCourse() && (
          <Actions.Button
            className={cx(
              styles.iconButton,
              showContinueEditing ? styles.continueEditing : styles.edit,
            )}
            icon="paperflite-edit"
            title={showContinueEditing ? "continue editing" : "edit"}
            width={showContinueEditing ? 4 : 2}
            tooltip={showContinueEditing ? continueEditingTooltip : undefined}
            onClick={handleEdit}
          />
        )}
      </>
    );
  };

  return (
    <div className={cx(styles.menubar, className)}>
      <Actions.Container>
        {mode === "viewer" && (
          <Actions.Row className={styles.row}>
            <ViewerActions />
          </Actions.Row>
        )}

        {mode === "editor" && !emptyPodcast && (
          <Actions.Row className={styles.row}>
            <Reveal show={errors > 0}>
              <Actions.Button
                className={cx(styles.iconButton, styles.errors)}
                icon="fa-solid fa-triangle-exclamation"
                width={2}
                title={`${errors}`}
                onClick={() => {
                  podcastContext.setShowValidationErrorDialog?.(true);
                }}
              />
            </Reveal>
            <Actions.Button
              className={cx(styles.iconButton, styles.save)}
              width={2}
              icon="paperflite-tick-unselected"
              title={updating ? "saving..." : "save"}
              disabled={updating || publishing || generating}
              onClick={handleSave}
            />

            <Actions.Button
              className={cx(styles.iconButton, styles.editStudio)}
              icon="paperflite-edit"
              title="edit in studio"
              disabled={generating || updating || publishing}
              onClick={handleEditStudio}
            />

            {canPublish &&
              <Actions.Button
                className={cx(styles.iconButton, styles.publish)}
                icon="paperflite-rocket"
                title={publishing ? "publishing..." : "publish"}
                disabled={!hasChanges || updating || publishing || generating}
                onClick={handlePublish}
              />
            }

            {showGenerateTranscriptButton &&
              <Actions.Button
                className={cx(styles.iconButton, styles.generatePodcast)}
                width={3}
                icon="paperflite-right-arrow"
                title={quickGenerating || processingTranscript ? "generating transcript..." : "generate"}
                disabled={!shouldGenerateTranscript || quickGenerating || processingTranscript || updating}
                onClick={handleGenerateTranscript}
              />
            }

            {showGenerateAudioButton &&
              <Actions.Button
                className={cx(styles.iconButton, styles.generatePodcast)}
                width={3}
                icon="paperflite-right-arrow"
                title={quickGenerating || processingAudio ? "generating audio..." : "generate"}
                disabled={!shouldGenerateAudio || quickGenerating || processingAudio || updating}
                onClick={handleGenerateAudio}
              />
            }

            <Actions.IconButton
              className={styles.iconButton}
              icon="paperflite-delete"
              tooltip="Delete Podcast"
              disabled={updating || publishing || generating}
              onClick={() => setShowDeleteDialog(true)}
            />

            {podcast?.status === "PUBLISHED" && (
              <Actions.Button
                className={styles.iconButton}
                width={3}
                icon="paperflite-close"
                title="discard changes"
                disabled={updating || publishing || generating}
                onClick={() => setShowDiscardDialog(true)}
              />
            )}
          </Actions.Row>
        )}

        {mode === "editor" && emptyPodcast && (
          <Actions.Row className={styles.row}>
            <Actions.Row className={styles.row}>
              <Actions.Button
                className={styles.iconButton}
                width={3}
                icon="paperflite-delete"
                title="cancel &amp; exit"
                onClick={() => setShowDeleteDialog(true)}
              />
            </Actions.Row>
          </Actions.Row>
        )}

      </Actions.Container>

      <ValidationErrorDialog
        show={showValidationErrorDialog}
        onClose={() => podcastContext.setShowValidationErrorDialog?.(false)}
      />

      <DeletePodcastDialog
        show={showDeleteDialog}
        podcast={podcast}
        onFinish={handleDeleteFinish}
        onClose={() => setShowDeleteDialog(false)}
      />

      <DiscardPodcastDialog
        show={showDiscardDialog}
        podcast={podcast}
        onFinish={handleDiscardFinish}
        onClose={() => setShowDiscardDialog(false)}
      />
    </div>
  );
};

interface StudioMenuBarProps {
  podcast: Podcast;
  editState: PodcastEditState;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;

  // eslint-disable-next-line no-unused-vars
  onSave?: () => void;
  // eslint-disable-next-line no-unused-vars
  onDiscard?: () => void;
  // eslint-disable-next-line no-unused-vars
  onGenerateTranscript?: () => void;
  // eslint-disable-next-line no-unused-vars
  onGenerateAudio?: () => void;
}

//Studio Menu
export const StudioMenuBar: React.FC<StudioMenuBarProps> = ({ podcast, editState, onSave, onDiscard, onGenerateTranscript, onGenerateAudio }) => {
  const id = podcast?.id;
  const [showValidationErrorDialog, setShowValidationErrorDialog] = React.useState(false);

  const errors = useSelector((state: any) => {
    const errors = _.get(state.coaching, "podcasts.edit.errors") as PodcastValidationError[];
    return _.size(errors);
  }, _.isEqual);

  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(
      state.coaching,
      `podcasts.update.['${id}'].updating`,
    ) as boolean;
    const updated = _.get(
      state.coaching,
      `podcasts.update.['${id}'].updated`,
    ) as boolean;

    return { updating, updated };
  });

  const transcriptChanged = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.editorSession.transcript.hasChanges`, false);
  });

  const shouldGenerateTranscript = useMemo(() => {
    const updatedPodcast = {
      ...podcast,
      assets: editState.assets,
      duration: editState.duration,
      type: editState.podcastType,
    };

    const request = preparePodcastConfigurationRequest(podcast, updatedPodcast);
    const hasChanges = _.keys(request).length > 0;

    if (hasChanges) {
      return true;
    }

    const needsProcessing = _.get(podcast, "transcriptProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    const failedStatus = _.get(podcast, "transcriptProcessing.status") === 'failed';
    if (failedStatus) {
      return true;
    }

    return false;
  }, [podcast, editState]);

  const shouldGenerateAudio = useMemo(() => {
    if (shouldGenerateTranscript) {
      return false;
    }

    const updatedPodcast = {
      ...podcast,
      voicePreset: editState.voicePreset!,
    };

    const request = preparePodcastConfigurationRequest(podcast, updatedPodcast);
    const hasChanges = _.keys(request).length > 0;

    if (hasChanges) {
      return true;
    }

    const needsProcessing = _.get(podcast, "audioProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    if(transcriptChanged){
      return true;
    }

    return false;
  }, [podcast, transcriptChanged, shouldGenerateTranscript, editState]);

  const quickGenerating = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const processingTranscript = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateTranscript.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.transcriptProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const processingAudio = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateAudio.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.audioProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });
  const showAudioError = useSelector((state: any) => {
  const podcast = _.get(state, "coaching.podcasts.edit.item");

  const processingAudio =
    _.get(state.coaching.podcasts, "edit.generateAudio.generating", false) ||
    _.includes(
      ["requested", "in_progress"],
      _.get(podcast, "audioProcessing.status")
    );

  const shouldGenerateAudio =
    _.get(podcast, "transcriptProcessing.status") === "pending" ||
    _.get(podcast, "audioProcessing.status") === "pending";

  const audioError = _.get(podcast, "audioProcessing.error", null);
  const emptyPodcast = !_.get(podcast, "transcriptUrl");

  return (
    !processingAudio &&
    !shouldGenerateAudio &&
    audioError &&
    !podcast?.audio?.url &&
    !emptyPodcast
  );
});

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  }

  const handleDiscard = () => {
    onDiscard?.();
  }

  const handleGenerateTranscript = () => {
    if (onGenerateTranscript) {
      onGenerateTranscript();
    }
  }

  const handleGenerateAudio = () => {
    if (onGenerateAudio) {
      onGenerateAudio();
    }
  }

  return (
    <div className={cx(styles.menubar)}>
      <Actions.Container>
        <Actions.Row className={styles.row}>
          <Reveal show={errors > 0}>
            <Actions.Button
              className={cx(styles.iconButton, styles.errors)}
              icon="fa-solid fa-triangle-exclamation"
              width={2}
              title={`${errors}`}
              onClick={() => {
                setShowValidationErrorDialog(true);
              }}
            />
          </Reveal>

          <Actions.Button
            className={cx(styles.iconButton, styles.save)}
            width={2}
            icon="paperflite-tick-unselected"
            title={updating ? "saving..." : "save"}
            disabled={updating}
            onClick={handleSave}
          />

          <Actions.Button
            className={cx(styles.iconButton, styles.discard)}
            width={3}
            icon="paperflite-close"
            title="discard"
            disabled={updating}
            onClick={handleDiscard}
          />

          {(shouldGenerateTranscript || processingTranscript) && (
            <Actions.Button
              className={cx(styles.iconButton, styles.generatePodcast)}
              width={3}
              icon="paperflite-right-arrow"
              title={quickGenerating || processingTranscript ? "generating transcript..." : "generate transcript"}
              disabled={!shouldGenerateTranscript || quickGenerating || processingTranscript}
              onClick={handleGenerateTranscript}
            />
          )}

          {showAudioError && (
            <Actions.Button
              className={cx(styles.iconButton, styles.generatePodcast)}
              width={3}
              icon="paperflite-right-arrow"
              title={processingAudio ? "re-generating audio..." : "re-generate audio"}
              disabled={processingAudio || quickGenerating || updating}
              onClick={handleGenerateAudio}
            />
          )}

          {!showAudioError && !(shouldGenerateTranscript || processingTranscript) && (
            <Actions.Button
              className={cx(styles.iconButton, styles.generatePodcast)}
              width={3}
              icon="paperflite-right-arrow"
              title={quickGenerating || processingAudio ? "generating audio..." : "generate audio"}
              disabled={!shouldGenerateAudio || quickGenerating || processingAudio}
              onClick={handleGenerateAudio}
            />
          )}
        </Actions.Row>

      </Actions.Container>

      <ValidationErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      />
    </div>
  );
};

interface ValidationErrorDialogProps {
  show: boolean;
  onClose: () => void;
}

const ValidationErrorDialog: React.FC<ValidationErrorDialogProps> = ({
  show,
  onClose,
}) => {
  const podcastContext = useContext(PodcastContext);
  const errors = useSelector((state: any) => {
    return _.get(state, "coaching.podcasts.edit.errors") as PodcastValidationError[];
  }, _.isEqual);

  const sections = useMemo(() => {
    const sections: any[] = [];
    const groupErrors = _.groupBy(errors, "type");
    _.forIn(groupErrors, (value, key) => {
      const content = value[0];
      const type = content.type;
      switch (type) {
        case "podcast":
          sections.push({
            name: "Podcast Details",
            errors: value,
          });
          break;

        default:
          break;
      }
    });

    return sections;
  }, [errors]);

  const handleFixClicked = (error: PodcastValidationError) => {
    podcastContext.gotoError?.(error);
    onClose();
  };

  return (
    <Slider.Dialog width={"550px"} show={show} onClose={onClose}>
      <Slider.Header
        title="review errors"
        message="we failed to publish your podcast. Fix these errors and train again."
      />
      <Slider.Body>
        <div className={styles.validationsBody}>
          {sections.map((section, index) => {
            return (
              <ValidationSection
                key={index}
                name={section.name}
                errors={section.errors}
                onFixClicked={handleFixClicked}
              />
            );
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button onClick={onClose}>Close</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface ValidationSectionProps {
  name: string;
  errors: any; //PodcastValidationError[];
  // eslint-disable-next-line no-unused-vars
  onFixClicked: (error: any) => void; //PodcastValidationError
}

const ValidationSection: React.FC<ValidationSectionProps> = ({
  name,
  errors,
  onFixClicked,
}) => {
  return (
    <div className={styles.validationSection}>
      <div className={styles.title}>{name}</div>
      <div className={styles.errors}>
        {errors.map((error, index) => {
          return (
            <div
              key={index}
              className={styles.error}
              onClick={() => onFixClicked(error)}
            >
              <div className={styles.message}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {error.message}
              </div>
              <div className={styles.fix}>
                <i className="paperflite-arrow-up"></i>
                fix now
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.separator}></div>
    </div>
  );
};

interface DeletePodcastDialogProps {
  show: boolean;
  podcast: Podcast;
  onFinish: () => void;
  onClose: () => void;
}

export const DeletePodcastDialog: React.FC<DeletePodcastDialogProps> = ({
  show,
  podcast,
  onFinish,
  onClose,
}) => {
  const dispatch = useDispatch();

  const deleting = useSelector((state: any) => {
    return (
      show &&
      (_.get(
        state.coaching,
        `podcasts.deletion['${podcast.id}'].deleting`,
      ) as boolean)
    );
  });

  const deleted = useSelector((state: any) => {
    return (
      show &&
      (_.get(
        state.coaching,
        `podcasts.deletion['${podcast.id}'].deleted`,
      ) as boolean)
    );
  });

  useEffect(() => {
    if (deleted) {
      dispatch(invalidatePodcastDeletionState(podcast.id!));
      onFinish();
    }
  }, [deleted]);

  const handleConfirm = () => {
    dispatch(deletePodcast(podcast.id!));
  };

  return (
    <Slider.Dialog width={"380px"} show={show} onClose={onClose}>
      <Slider.Header title="deleting..." message={podcast?.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to delete this podcast? This action cannot be
            undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          disabled={deleting}
          onClick={handleConfirm}
        >
          {deleting ? "Deleting..." : "Confirm"}
        </Button>
        <Button disabled={deleting} onClick={onClose}>
          Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface DiscardPodcastDialogProps {
  show: boolean;
  podcast: Podcast;
  onFinish: () => void;
  onClose: () => void;
}

const DiscardPodcastDialog: React.FC<DiscardPodcastDialogProps> = ({
  show,
  podcast,
  onFinish,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(
      state.coaching,
      `podcasts.update.['${podcast.id}'].updating`,
    ) as boolean;
    const updated = _.get(
      state.coaching,
      `podcasts.update.['${podcast.id}'].updated`,
    ) as boolean;

    return { updating, updated };
  });

  useEffect(() => {
    if (updated) {
      dispatch(invalidatePodcastUpdateState());
      onFinish();
    }
  }, [updated]);

  const handleConfirm = () => {
    dispatch(discardPodcastDraft(podcast.id!));
  };

  return (
    <Slider.Dialog width={"380px"} show={show} onClose={onClose}>
      <Slider.Header title="Discarding draft..." message={podcast.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to discard changes to this podcast? This
            action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          disabled={updating}
          onClick={handleConfirm}
        >
          {updating ? "Discarding..." : "Confirm"}
        </Button>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};
